/**
 * One tab plays at a time. Every tab holds its own copy of the game and
 * writes the same save, so two open tabs overwrote each other: progress in
 * one was lost to the other's next autosave, and a wipe in one came back.
 *
 * The browser's Web Locks decide who plays. A tab starts the game only once
 * it holds the lock; any other tab shows a panel saying the game is open
 * elsewhere, with Play here. Play here asks the playing tab (over a
 * BroadcastChannel) to save and stand down, waits for the lock it lets go,
 * and only then starts, so it loads the save that was just written. Closing
 * or crashing the playing tab frees the lock by itself.
 *
 * A browser without Web Locks (older than Safari 15.4, Chrome 69, Firefox 96)
 * just plays, as every tab did before.
 */

import { liftLoader } from './loader';

const LOCK = 'dinoclicker.game';
const CHANNEL = 'dinoclicker.tabs';
/** Set across a reload by a tab that stood down and then asked to play again. */
const TAKEOVER_KEY = 'dinoclicker.takeover';

interface Game {
  standDown(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function session(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function runInOneTab(start: () => Game): void {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks || typeof BroadcastChannel === 'undefined') {
    start();
    return;
  }

  const backdrop = el('div', 'modal');
  backdrop.hidden = true;
  const panel = el('div', 'modal__panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  const title = el('h2', 'modal__title', 'DinoClicker is open in another tab');
  const body = el(
    'p',
    'modal__body',
    'It plays in one tab at a time, so two tabs can never save over each other.',
  );
  const button = el('button', 'modal__button', 'Play here');
  button.type = 'button';
  panel.append(title, body, button);
  backdrop.append(panel);
  document.body.append(backdrop);

  const channel = new BroadcastChannel(CHANNEL);
  let game: Game | null = null;
  /** Lets go of the lock; set while this tab holds it. */
  let release: (() => void) | null = null;
  /** This page's game ran and stood down, so playing again needs a fresh page. */
  let stale = false;

  function showPanel(): void {
    // No game is starting behind it, so nothing else would lift the loader.
    liftLoader();
    button.disabled = false;
    button.textContent = 'Play here';
    backdrop.hidden = false;
    button.focus();
  }

  /** Holding the lock: start the game, and keep the lock until told to go. */
  function play(): Promise<void> {
    backdrop.hidden = true;
    game = start();
    return new Promise<void>((resolve) => {
      release = resolve;
    });
  }

  function takeOver(): void {
    if (stale) {
      // The game on this page stopped with the state it had then; start
      // clean from the save instead of bringing it back.
      session()?.setItem(TAKEOVER_KEY, '1');
      window.location.reload();
      return;
    }
    button.disabled = true;
    button.textContent = 'Taking over…';
    backdrop.hidden = false;
    channel.postMessage('release');
    // Waits for the playing tab to save and let go.
    void locks!.request(LOCK, play);
  }

  channel.addEventListener('message', (ev: MessageEvent) => {
    if (ev.data !== 'release' || !game) return;
    game.standDown();
    game = null;
    stale = true;
    showPanel();
    release?.();
    release = null;
  });
  button.addEventListener('click', takeOver);

  const store = session();
  if (store?.getItem(TAKEOVER_KEY)) {
    store.removeItem(TAKEOVER_KEY);
    takeOver();
    return;
  }
  // A refresh can ask before the page it replaces has let go, so a lock that
  // is taken is asked for once more before the panel says so.
  const tryToPlay = (again: boolean): void => {
    void locks.request(LOCK, { ifAvailable: true }, (lock) => {
      if (lock) return play();
      if (again) window.setTimeout(() => tryToPlay(false), 400);
      else showPanel();
      return undefined;
    });
  };
  tryToPlay(true);
}
